import React, { forwardRef, HTMLAttributes } from 'react';
import { isMobile } from 'react-device-detect';
import styled from 'styled-components';
import colors from '../../constants/colors.constants';

export interface SelectProps extends Omit<
  HTMLAttributes<HTMLSelectElement>,
  'onChange'
> {
  value?: string;
  onChange?: (value: string) => void;
  options: { label: string; value: string }[];
  borderless?: boolean;
  placeholder?: string;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>((props, ref) => {
  const {
    options,
    value,
    onChange,
    borderless = false,
    placeholder,
    style,
    ...rest
  } = props;

  return (
    <div className="relative w-full">
      <StyledSelect
        ref={ref}
        value={value}
        onChange={(e) => {
          const { target } = e;
          const { value } = target;
          onChange?.(value);
        }}
        $isMobile={isMobile}
        $activeColor={colors.primary3}
        $borderDefaultColor={colors.greyHover}
        $borderless={borderless}
        style={style}
        {...rest}
      >
        {placeholder && (
          <option disabled hidden selected>
            {placeholder}
          </option>
        )}
        {options.map((option, index) => (
          <option key={index} value={option.value}>
            {option.label}
          </option>
        ))}
      </StyledSelect>
    </div>
  );
});

export default Select;

const StyledSelect = styled.select<{
  $borderDefaultColor: string;
  $activeColor: string;
  $isMobile: boolean;
  $borderless: boolean;
}>`
  height: ${(props) => {
    if (props.$isMobile) {
      return '48px';
    }
    return '40px';
  }};
  padding: 0 16px;
  font-size: 16px;
  font-weight: 300;
  font-family: Pretendard;
  appearance: none;

  border: ${(props) => {
    if (props.$borderless) {
      return 'none';
    }

    return `1px solid ${props.$borderDefaultColor}`;
  }};

  &:focus {
    outline: none;
    border: ${(props) => {
      if (props.$borderless) {
        return 'none';
      }
      return `1px solid ${props.$activeColor}`;
    }};
  }

  &:hover {
    border: ${(props) => {
      if (props.$borderless) {
        return 'none';
      }
      return `1px solid ${props.$activeColor}`;
    }};
  }

  transition: border 0.2s;
`;
